import { all, call, put, takeLatest } from 'redux-saga/effects';
import {
    addScheme,
    deleteScheme,
    editScheme,
    editSchemePreview,
    fetchSchemePreview,
    getAllSchemes,
    getBookingDevicesAvailableForSchemes,
    getBookingDevicesRequiredForScheme,
    getSchemeById,
    getSchemes,
    getTrexStatus,
    startTrex,
    stopTrex,
} from '@pages/schemes/api';
import {
    addSchemeError,
    addSchemeRequest,
    addSchemeSuccess,
    deleteSchemeError,
    deleteSchemeRequest,
    deleteSchemeSuccess,
    editSchemeError,
    editSchemeRequest,
    editSchemeSuccess,
    getAllSchemesError,
    getAllSchemesRequest,
    getAllSchemesSuccess,
    getDevicesAvailableForSchemesError,
    getDevicesAvailableForSchemesRequest,
    getDevicesAvailableForSchemesSuccess,
    getDevicesRequiredForSchemeError,
    getDevicesRequiredForSchemeRequest,
    getDevicesRequiredForSchemeSuccess,
    getSchemeByIdError,
    getSchemeByIdRequest,
    getSchemeByIdSuccess,
    getSchemesError,
    getSchemesRequest,
    getSchemesSuccess,
    getTrexStatusError,
    getTrexStatusRequest,
    getTrexStatusSuccess,
    startTrexError,
    startTrexRequest,
    startTrexSuccess,
    stopTrexError,
    stopTrexRequest,
    stopTrexSuccess,
} from './index';
import { addTag } from '@features/tags/api/tagsApi';
import { transformBookingDevicesResponse } from '@pages/booking/utils';

export function* getSchemesSaga(params) {
    try {
        const response = yield call(getSchemes, params.payload);
        yield put(getSchemesSuccess(response));
    } catch (error) {
        yield put(getSchemesError(error));
    }
}

export function* getAllSchemesSaga(params) {
    const { module } = params.payload;

    try {
        const response = yield call(getAllSchemes, module);
        yield put(getAllSchemesSuccess(response));
    } catch (error) {
        yield put(getAllSchemesError(error));
    }
}

export function* getSchemeByIdSaga(params) {
    try {
        let response = yield call(getSchemeById, params.payload);
        yield put(getSchemeByIdSuccess(response));
    } catch (error) {
        yield put(getSchemeByIdError(error));
    }
}

export function* addSchemeSaga(params) {
    const requestParams = params.payload;

    try {
        if (Array.isArray(requestParams.tags)) {
            for (const tag of requestParams.tags) {
                if (tag.wasAdded === undefined) continue;
                yield call(addTag, tag.text, 'schemes');
            }
        }

        const { preview, ...requestParamsWithoutPreview } = requestParams;
        const { data } = yield call(addScheme, requestParamsWithoutPreview);
        let previewBlob;

        if (typeof preview === 'string') {
            previewBlob = yield call(fetchSchemePreview, preview);
        } else if (preview instanceof File) {
            previewBlob = preview;
        }

        if (previewBlob) {
            yield call(editSchemePreview, data.pk, previewBlob);
        }

        yield put(addSchemeSuccess(data));
    } catch (error) {
        yield put(addSchemeError(error));
    }
}

export function* editSchemeSaga(params) {
    const requestParams = params.payload.params;
    const id = params.payload.id;

    try {
        if (Array.isArray(requestParams.tags)) {
            for (const tag of requestParams.tags) {
                if (tag.wasAdded === undefined) continue;
                yield call(addTag, tag.text, 'schemes');
            }
        }

        const { data } = yield call(editScheme, id, requestParams);

        if (requestParams.preview) {
            yield call(editSchemePreview, id, requestParams.preview);
        }

        yield put(editSchemeSuccess(data));
    } catch (error) {
        yield put(editSchemeError(error));
    }
}

export function* deleteSchemeSaga(params) {
    const { id } = params.payload;
    try {
        yield call(deleteScheme, id);
        yield put(deleteSchemeSuccess(id));
    } catch (error) {
        yield put(deleteSchemeError(error));
    }
}

export function* getTrexStatusSaga(action) {
    try {
        const response = yield call(getTrexStatus, action.payload);
        yield put(getTrexStatusSuccess(response));
    } catch (error) {
        yield put(getTrexStatusError(error));
    }
}

export function* startTrexSaga(action) {
    try {
        const response = yield call(startTrex, action.payload);
        yield put(startTrexSuccess(response));
    } catch (error) {
        yield put(startTrexError(error));
    }
}

export function* stopTrexSaga(action) {
    try {
        const response = yield call(stopTrex, action.payload);
        yield put(stopTrexSuccess(response));
    } catch (error) {
        yield put(stopTrexError(error));
    }
}

export function* getDevicesAvailableForSchemesSaga() {
    try {
        const res = yield call(getBookingDevicesAvailableForSchemes);
        yield put(getDevicesAvailableForSchemesSuccess(res));
    } catch (error) {
        yield put(getDevicesAvailableForSchemesError(error));
    }
}

export function* getDevicesRequiredForSchemeSaga(action) {
    const schemeId = action.payload;

    try {
        const res = yield call(getBookingDevicesRequiredForScheme, schemeId);
        yield put(getDevicesRequiredForSchemeSuccess({ ...res, data: transformBookingDevicesResponse(res) }));
    } catch (error) {
        yield put(getDevicesRequiredForSchemeError(error));
    }
}

export default function* schemesSagaWatcher() {
    yield all([
        takeLatest(getTrexStatusRequest, getTrexStatusSaga),
        takeLatest(startTrexRequest, startTrexSaga),
        takeLatest(stopTrexRequest, stopTrexSaga),
        takeLatest(getSchemesRequest, getSchemesSaga),
        takeLatest(getAllSchemesRequest, getAllSchemesSaga),
        takeLatest(getSchemeByIdRequest, getSchemeByIdSaga),
        takeLatest(addSchemeRequest, addSchemeSaga),
        takeLatest(editSchemeRequest, editSchemeSaga),
        takeLatest(deleteSchemeRequest, deleteSchemeSaga),
        takeLatest(getDevicesAvailableForSchemesRequest, getDevicesAvailableForSchemesSaga),
        takeLatest(getDevicesRequiredForSchemeRequest, getDevicesRequiredForSchemeSaga),
    ]);
}
